import { getScrollTop, getScrollEventTarget } from '@theme/scroll-container';

const desktop = matchMedia('(min-width: 750px)');

function onFocus(event) {
  const input =  (event.target);
  const focused = input.dataset.placeholderFocus;
  if (!focused) return;

  input.dataset.placeholderRest ??= input.placeholder;
  input.placeholder = focused;
}

function onBlur(event) {
  const input =  (event.target);
  const rest = input.dataset.placeholderRest;

  if (rest == null || input.value !== '') return;
  input.placeholder = rest;
}

function siblingsOf(details) {
  const row = details.closest('.group-block-content');
  if (!row || !row.closest('.shopify-section-group-footer-group')) return [];

  return [...row.querySelectorAll('details.menu__details')].filter(
    (other) => other !== details && other instanceof HTMLDetailsElement && other.open
  );
}

function onToggle(event) {
  const details =  (event.target);

  if (desktop.matches || !details.open) return;

  const others = siblingsOf(details);
  if (others.length === 0) return;

  const before = details.getBoundingClientRect().top;
  for (const other of others) other.open = false;
  const shift = details.getBoundingClientRect().top - before;

  if (shift !== 0) window.scrollBy({ top: shift, left: 0, behavior: 'instant' });
}

let footerObserver;
let syncFooter;

// A short page shows the footer before any scrolling; the header only steps aside once the
// shopper has scrolled past it.
function watchFooter() {
  const footer = document.querySelector('.shopify-section-group-footer-group');
  const header = document.querySelector('#header-component');
  if (!footer || !header) return;

  let footerInView = false;
  const scrollTarget = getScrollEventTarget();
  footerObserver?.disconnect();
  if (syncFooter) scrollTarget.removeEventListener('scroll', syncFooter);

  syncFooter = () => {
    header.toggleAttribute('data-sod-at-footer', footerInView && getScrollTop() > header.offsetHeight);
  };
  footerObserver = new IntersectionObserver(
    ([entry]) => {
      footerInView = entry.isIntersecting;
      syncFooter();
    },
    { threshold: 0 }
  );
  footerObserver.observe(footer);
  scrollTarget.addEventListener('scroll', syncFooter, { passive: true });
}

async function submitEmailSignup(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('email-signup__form')) return;

  // Stop Shopify's native customer-form redirect before doing any async work.
  event.preventDefault();

  if (!form.reportValidity() || form.dataset.submitting === 'true') return;

  const message = form.querySelector('[data-email-signup-message]');
  const input = form.querySelector('input[type="email"]');
  const button = form.querySelector('button[type="submit"]');

  form.dataset.submitting = 'true';
  if (message) {
    message.hidden = true;
    message.textContent = '';
  }
  if (button) {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
  }

  try {
    const response = await fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
    });
    const html = await response.text();
    const page = new DOMParser().parseFromString(html, 'text/html');
    const responseUrl = new URL(response.url, window.location.origin);
    const succeeded =
      response.ok &&
      (responseUrl.searchParams.get('customer_posted') === 'true' ||
        page.querySelector('[id^="Email-signup__message-success-"]'));

    if (!succeeded) {
      const serverError = page.querySelector('.email-signup__message .icon-error')?.closest('.email-signup__message');
      throw new Error(serverError?.textContent?.trim() || 'Unable to subscribe. Please try again.');
    }

    form.reset();
    input?.removeAttribute('aria-invalid');
    if (message) {
      message.textContent = message.dataset.successMessage;
      message.classList.remove('email-signup__message--error');
      message.hidden = false;
      message.focus({ preventScroll: true });
    }
  } catch (error) {
    input?.setAttribute('aria-invalid', 'true');
    if (message) {
      message.textContent = error instanceof Error ? error.message : 'Unable to subscribe. Please try again.';
      message.classList.add('email-signup__message--error');
      message.hidden = false;
      message.focus({ preventScroll: true });
    }
  } finally {
    delete form.dataset.submitting;
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

// The accessiBe widget's floating button is hidden (sod-theme.css), so a menu link marked as its
// trigger opens the widget by clicking that button inside its shadow root. A link that points at
// "#" would otherwise also jump the page to the top.
function onAccessibilityTrigger(event) {
  const trigger = event.target instanceof Element && event.target.closest('a[data-acsb-custom-trigger]');
  if (!trigger?.getAttribute('href')?.startsWith('#')) return;

  event.preventDefault();
  for (const widget of document.querySelectorAll('access-widget-ui')) {
    const button = widget.shadowRoot?.querySelector('[part~="acsb-trigger"]');
    if (button instanceof HTMLElement) {
      button.click();
      return;
    }
  }
}

window.sodEmailSignupSubmit = submitEmailSignup;
watchFooter();

document.addEventListener('shopify:section:load', watchFooter);

document.addEventListener('focusin', onFocus);
document.addEventListener('focusout', onBlur);

document.addEventListener('toggle', onToggle, { capture: true });
document.addEventListener('submit', submitEmailSignup, { capture: true });
document.addEventListener('click', onAccessibilityTrigger);
